import { compareTrackingOrders } from '@/lib/utils/tracking-order';
import { supabase } from '@/lib/supabase/client';
import type { LocationOrder, LocationOrderInsert, LocationOrderUpdate } from '@/types/location-orders';
import { locationLaunchesService } from './location-launches';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const locationOrdersService = {
  async getAll(): Promise<LocationOrder[]> {
    try {
      const { data, error } = await supabase.from('location_orders').select('*').order('order_date', { ascending: false });

      if (error) {
        console.error('Error fetching location orders:', error);
        return [];
      }

      return (data as LocationOrder[]) || [];
    } catch (error) {
      console.error('Error fetching location orders:', error);
      return [];
    }
  },

  async getById(id: string): Promise<LocationOrder | null> {
    try {
      const { data, error } = await supabase.from('location_orders').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching location order:', error);
        return null;
      }

      return (data as LocationOrder) || null;
    } catch (error) {
      console.error('Error fetching location order:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<LocationOrder[]> {
    try {
      const { data, error } = await supabase
        .from('location_orders')
        .select('*')
        .eq('location_id', locationId)
        .order('order_date', { ascending: false }).order('id', { ascending: true });

      if (error) {
        console.error('Error fetching location orders by location ID:', error);
        return [];
      }

      return (data as LocationOrder[]) || [];
    } catch (error) {
      console.error('Error fetching location orders by location ID:', error);
      return [];
    }
  },

  async getByLaunchId(launchId: string): Promise<LocationOrder[]> {
    try {
      const { data, error } = await supabase
        .from('location_orders')
        .select('*')
        .eq('launch_id', launchId)
        .order('order_date', { ascending: false });

      if (error) {
        console.error('Error fetching location orders by launch ID:', error);
        return [];
      }

      return (data as LocationOrder[]) || [];
    } catch (error) {
      console.error('Error fetching location orders by launch ID:', error);
      return [];
    }
  },

  async create(data: LocationOrderInsert): Promise<LocationOrder> {
    try {
      const userId = await getCurrentUserId();

      const insertData = {
        location_id: data.location_id,
        deal_id: data.deal_id ?? null,
        launch_id: data.launch_id ?? null,
        order_date: data.order_date,
        status: data.status ?? 'pending',
        total_amount: data.total_amount ?? null,
        paid_amount: data.paid_amount ?? null,
        paid_date: data.paid_date ?? null,
        notes: data.notes ?? null,
        created_by: userId,
      };

      const { data: newOrder, error } = await supabase.from('location_orders').insert(insertData).select().single();

      if (error) {
        console.error('Error creating location order:', error);
        throw new Error(`Failed to create location order: ${error.message}`);
      }

      return newOrder as LocationOrder;
    } catch (error) {
      console.error('Error creating location order:', error);
      throw error;
    }
  },

  async createFromDeal(deal: { id: string; location_id: string; total_contract_value: number; name: string }): Promise<LocationOrder> {
    try {
      // Check if order already exists for this deal (idempotency)
      const existingOrders = await this.getByDealId(deal.id);
      if (existingOrders.length > 0) {
        // Return the first existing order
        return existingOrders[0] as LocationOrder;
      }

      const userId = await getCurrentUserId();
      const today = new Date().toISOString().split('T')[0];
      const primaryLaunch = await locationLaunchesService.getPrimaryByLocationId(deal.location_id);

      const insertData = {
        location_id: deal.location_id,
        deal_id: deal.id,
        launch_id: primaryLaunch?.id ?? null,
        order_date: today,
        status: 'pending' as const,
        total_amount: deal.total_contract_value,
        paid_amount: null,
        paid_date: null,
        notes: `Auto-created from closed deal: ${deal.name}`,
        created_by: userId,
      };

      const { data: newOrder, error } = await supabase.from('location_orders').insert(insertData).select().single();

      if (error) {
        console.error('Error creating location order from deal:', error);
        throw new Error(`Failed to create location order from deal: ${error.message}`);
      }

      return newOrder as LocationOrder;
    } catch (error) {
      console.error('Error creating location order from deal:', error);
      throw error;
    }
  },

  async getByDealId(dealId: string): Promise<LocationOrder[]> {
    try {
      const { data, error } = await supabase
        .from('location_orders')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching location orders by deal ID:', error);
        return [];
      }

      return (data as LocationOrder[]) || [];
    } catch (error) {
      console.error('Error fetching location orders by deal ID:', error);
      return [];
    }
  },

  async update(id: string, data: LocationOrderUpdate): Promise<LocationOrder> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.location_id !== undefined) updateData.location_id = data.location_id;
      if (data.deal_id !== undefined) updateData.deal_id = data.deal_id;
      if (data.launch_id !== undefined) updateData.launch_id = data.launch_id;
      if (data.order_date !== undefined) updateData.order_date = data.order_date;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.total_amount !== undefined) updateData.total_amount = data.total_amount;
      if (data.paid_amount !== undefined) updateData.paid_amount = data.paid_amount;
      if (data.paid_date !== undefined) updateData.paid_date = data.paid_date;
      if (data.notes !== undefined) updateData.notes = data.notes;

      const { data: updatedOrder, error } = await supabase
        .from('location_orders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating location order:', error);
        throw new Error(`Failed to update location order: ${error.message}`);
      }

      if (!updatedOrder) {
        throw new Error('Location order not found');
      }

      return updatedOrder as LocationOrder;
    } catch (error) {
      console.error('Error updating location order:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('location_orders').delete().eq('id', id);

      if (error) {
        console.error('Error deleting location order:', error);
        throw new Error(`Failed to delete location order: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting location order:', error);
      throw error;
    }
  },

  async getOrCreateTrackingOrderForLaunch(launchId: string): Promise<LocationOrder> {
    const existingOrders = await this.getByLaunchId(launchId);
    const sortedExistingOrders = [...existingOrders].sort(compareTrackingOrders);
    const existingOrder = sortedExistingOrders[0];
    if (existingOrder) {
      return existingOrder;
    }

    const launch = await locationLaunchesService.getById(launchId);
    if (!launch) {
      throw new Error('Launch not found');
    }

    const orderDate = new Date().toISOString().split('T')[0] || new Date().toISOString();
    return this.create({
      location_id: launch.location_id,
      launch_id: launchId,
      order_date: orderDate,
      status: 'pending',
      notes: 'Created for launch fulfillment tracking',
    });
  },
};
